import { useEffect, useMemo, useRef, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, ListChecks, ChevronLeft, ChevronRight } from "lucide-react";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { Order, formatMoney, formatTime } from "#orders.ts";
import { useAuth } from "#auth.tsx";
import { ApiError } from "#api.ts";
import { useOrders } from "#orders-context.tsx";
import { useLanguage } from "#i18n.tsx";
import { StaffDialog } from "#order-dialog.tsx";
import { useViewSearch } from "#use-view-search.ts";
import {
  historyPageSizes,
  readHistoryView,
  writeHistoryView,
} from "#history-view-search.ts";
import type { OrderView } from "#order-view-search.ts";
import {
  bulkStatusSchema,
  changeOrderStatuses,
  type BulkStatusResult,
} from "#bulk-order-status.ts";
import { runEffectMutation } from "#effect-query.ts";

function SelectionBox({
  label,
  checked,
  mixed = false,
  change,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly mixed?: boolean;
  readonly change: (checked: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return (
    <label className="table-selection">
      <input
        ref={ref}
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={(event) => change(event.target.checked)}
      />
    </label>
  );
}

/** A page-scoped, searchable history table. Selection captures revisions, never just row positions. */
export function OrderHistoryTable({
  orders,
  status,
  statuses,
  changeStatus,
  openOrder,
  hasOlder,
}: {
  readonly orders: readonly Order[];
  readonly status: OrderView["status"];
  readonly statuses: readonly OrderView["status"][];
  readonly changeStatus: (status: OrderView["status"]) => void;
  readonly openOrder: (id: string) => void;
  readonly hasOlder: boolean;
}) {
  const { t, language, orderStatus } = useLanguage();
  const [params, applySearch] = useViewSearch();
  const view = readHistoryView(params);
  const scope = params.toString();
  const [search, setSearch] = useState(view.search);
  const [selection, setSelection] = useState<{
    readonly scope: string;
    readonly orders: readonly Order[];
  }>({ scope, orders: [] });
  const [batch, setBatch] = useState<readonly Order[]>();
  const selected = selection.scope === scope ? selection.orders : [];
  useEffect(() => {
    setSearch(view.search);
  }, [view.search]);
  useEffect(() => {
    setSelection({ scope, orders: [] });
  }, [scope]);
  const data = useMemo(() => [...orders], [orders]);
  const columns = useMemo<ColumnDef<Order>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <SelectionBox
            label={t("history.selectPage")}
            checked={table.getIsAllPageRowsSelected()}
            mixed={table.getIsSomePageRowsSelected()}
            change={(value) => table.toggleAllPageRowsSelected(value)}
          />
        ),
        cell: ({ row }) => (
          <SelectionBox
            label={t("history.selectOrder", { label: row.original.label })}
            checked={row.getIsSelected()}
            change={(value) => row.toggleSelected(value)}
          />
        ),
      },
      {
        id: "pickup",
        accessorFn: (order) => order.label,
        header: t("orders.pickup"),
        cell: ({ row }) => (
          <>
            <strong>{row.original.label}</strong>
            <small>{row.original.squareId.slice(-8)}</small>
          </>
        ),
      },
      {
        id: "status",
        header: t("orders.status"),
        cell: ({ row }) => (
          <span className={`pill ${row.original.state}`}>
            {orderStatus(row.original.state)}
          </span>
        ),
      },
      {
        id: "items",
        header: t("history.items"),
        cell: ({ row }) => (
          <span className="history-items">
            {row.original.lines
              .map(
                (line) =>
                  `${line.quantity} × ${line.kitchenName ?? line.name ?? t("orders.item")}`,
              )
              .join(", ")}
          </span>
        ),
      },
      {
        id: "created",
        header: t("orders.created"),
        cell: ({ row }) => formatTime(row.original.createdAt, true, language),
      },
      {
        id: "total",
        header: t("orders.total"),
        cell: ({ row }) => formatMoney(row.original.total),
      },
      {
        id: "details",
        header: t("orders.details"),
        cell: ({ row }) => (
          <button
            className="secondary"
            onClick={() => openOrder(row.original.id)}
          >
            {t("orders.open")}
          </button>
        ),
      },
    ],
    [language, openOrder, orderStatus, t],
  );
  // oxlint-disable-next-line react/incompatible-library -- TanStack Table v8 uses mutable instance APIs; this app does not enable React Compiler, and the instance is not passed to memoized consumers.
  const table = useReactTable({
    data,
    columns,
    getRowId: (order) => order.id,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    state: {
      globalFilter: view.search,
      pagination: { pageIndex: view.page - 1, pageSize: view.pageSize },
      rowSelection: Object.fromEntries(
        selected.map((order) => [order.id, true]),
      ),
    },
    getColumnCanGlobalFilter: (column) => column.id === "pickup",
    globalFilterFn: (row) => {
      const order = row.original;
      const text = [
        order.id,
        order.squareId,
        order.label,
        ...order.lines.flatMap((line) => [
          line.name,
          line.kitchenName,
          line.variationName,
        ]),
      ]
        .join(" ")
        .toLocaleLowerCase();
      return text.includes(view.search.toLocaleLowerCase());
    },
    onRowSelectionChange: (update) => {
      const current = Object.fromEntries(
        selected.map((order) => [order.id, true]),
      );
      const next = typeof update === "function" ? update(current) : update;
      setSelection({
        scope,
        orders: data
          .filter((order) => next[order.id])
          .map(
            (order) =>
              selected.find((snapshot) => snapshot.id === order.id) ?? order,
          ),
      });
    },
  });
  const pageRows = table.getRowModel().rows;
  const visibleIds = useMemo(() => pageRows.map((row) => row.id), [pageRows]);
  useEffect(() => {
    setSelection((current) => {
      const remaining = current.orders.filter((order) =>
        visibleIds.includes(order.id),
      );
      return remaining.length === current.orders.length
        ? current
        : { ...current, orders: remaining };
    });
  }, [visibleIds]);
  const chosen = selected.filter((order) => visibleIds.includes(order.id));
  const pageCount = Math.max(1, table.getPageCount());
  useEffect(() => {
    if (view.page > pageCount)
      applySearch(writeHistoryView(params, { ...view, page: pageCount }), {
        replace: true,
      });
  }, [view.page, pageCount, params, view, applySearch]);
  const labelFor = (value: OrderView["status"]) =>
    value === "active"
      ? t("orders.active")
      : value === "all"
        ? t("orders.all")
        : orderStatus(value);
  return (
    <section className="history-panel" aria-label={t("orders.historyTitle")}>
      <div className="history-tools">
        <label className="history-status-filter">
          <span>{t("orders.status")}</span>
          <select
            aria-label={t("history.quickStatus")}
            value={status}
            onChange={(event) => {
              const next = statuses.find(
                (value) => value === event.target.value,
              );
              if (next) changeStatus(next);
            }}
          >
            {statuses.map((value) => (
              <option key={value} value={value}>
                {labelFor(value)}
              </option>
            ))}
          </select>
        </label>
        <form
          className="history-search"
          onSubmit={(event) => {
            event.preventDefault();
            applySearch(writeHistoryView(params, { ...view, search, page: 1 }));
          }}
        >
          <Search aria-hidden="true" />
          <input
            type="search"
            aria-label={t("history.search")}
            placeholder={t("history.search")}
            maxLength={200}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <button className="secondary" type="submit">
            {t("history.searchAction")}
          </button>
        </form>
        <button
          className="primary history-bulk"
          disabled={chosen.length === 0}
          onClick={() => setBatch([...chosen])}
        >
          <ListChecks aria-hidden="true" />
          {t("history.bulk", { count: chosen.length })}
        </button>
      </div>
      <div className="history-context">
        <span>
          {t("history.loaded", {
            count: table.getFilteredRowModel().rows.length,
          })}
          {hasOlder ? ` · ${t("history.moreAvailable")}` : ""}
        </span>
        {view.search && (
          <button
            className="filter-chip"
            onClick={() =>
              applySearch(
                writeHistoryView(params, { ...view, search: "", page: 1 }),
              )
            }
          >
            {t("history.clearSearch")} ×
          </button>
        )}
        {chosen.length > 0 && (
          <button
            className="filter-chip"
            onClick={() => setSelection({ scope, orders: [] })}
          >
            {t("history.clearSelection", { count: chosen.length })} ×
          </button>
        )}
      </div>
      <div className="table-scroll">
        <table className="orders-table history-table">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={`${header.column.id}-column`}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr
                key={row.id}
                className={row.getIsSelected() ? "selected" : ""}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={`${cell.column.id}-column`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {pageRows.length === 0 && (
          <p className="history-empty">{t("history.noMatches")}</p>
        )}
      </div>
      <footer className="history-pagination">
        <label>
          {t("history.pageSize")}
          <select
            value={view.pageSize}
            onChange={(event) => {
              const pageSize = historyPageSizes.find(
                (size) => String(size) === event.target.value,
              );
              if (pageSize)
                applySearch(
                  writeHistoryView(params, { ...view, page: 1, pageSize }),
                );
            }}
          >
            {historyPageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <span>
          {t("history.page", {
            page: Math.min(view.page, pageCount),
            count: pageCount,
          })}
        </span>
        <div className="button-row">
          <button
            className="secondary"
            aria-label={t("history.previous")}
            disabled={view.page <= 1}
            onClick={() =>
              applySearch(
                writeHistoryView(params, { ...view, page: view.page - 1 }),
              )
            }
          >
            <ChevronLeft aria-hidden="true" />
          </button>
          <button
            className="secondary"
            aria-label={t("history.next")}
            disabled={view.page >= pageCount}
            onClick={() =>
              applySearch(
                writeHistoryView(params, { ...view, page: view.page + 1 }),
              )
            }
          >
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </footer>
      {batch && (
        <BulkStatusDialog
          orders={batch}
          close={() => setBatch(undefined)}
          completed={() => setSelection({ scope, orders: [] })}
        />
      )}
    </section>
  );
}

function BulkStatusDialog({
  orders,
  close,
  completed,
}: {
  readonly orders: readonly Order[];
  readonly close: () => void;
  readonly completed: () => void;
}) {
  const { api } = useAuth();
  const live = useOrders();
  const queryClient = useQueryClient();
  const { t, orderStatus } = useLanguage();
  const [status, setStatus] =
    useState<typeof bulkStatusSchema.Type>("order_completed");
  const started = useRef(false);
  const mutation = useMutation<readonly BulkStatusResult[], ApiError>({
    retry: false,
    mutationFn: () =>
      runEffectMutation(changeOrderStatuses(api, { orders, status })),
    onSuccess: (results) => {
      live.merge(
        results.flatMap((result) =>
          result._tag === "Updated" ? [result.saved.order] : [],
        ),
      );
      if (
        results.some(
          (result) =>
            result._tag === "Updated" && result.saved.liveUpdatesDelayed,
        )
      )
        live.setNotice(t("dialog.saveDelayed"));
      completed();
      void queryClient.invalidateQueries({ queryKey: ["orders", "history"] });
      if (results.some((result) => result._tag === "Failed"))
        void live.refresh();
    },
  });
  const results = mutation.data;
  const failures = results?.filter((result) => result._tag === "Failed") ?? [];
  return (
    <StaffDialog
      title={t(results ? "history.results" : "history.confirmTitle", {
        count: orders.length,
      })}
      close={mutation.isPending ? () => undefined : close}
      presentation="confirmation"
    >
      <div
        className="dialog-body bulk-dialog-body"
        aria-busy={mutation.isPending}
      >
        {results ? (
          <>
            <p
              className={failures.length ? "alert" : "bulk-success"}
              role={failures.length ? "alert" : "status"}
            >
              {t("history.resultCount", {
                updated: results.filter((result) => result._tag === "Updated")
                  .length,
                failed: failures.length,
                skipped: results.filter((result) => result._tag === "Unchanged")
                  .length,
              })}
            </p>
            {failures.length > 0 && <p>{t("history.failureHelp")}</p>}
            <ul className="bulk-results">
              {results.map((result) => (
                <li key={result.before.id}>
                  <strong>{result.before.label}</strong>
                  <span>
                    {result._tag === "Updated"
                      ? orderStatus(result.saved.order.state)
                      : result._tag === "Unchanged"
                        ? t("history.unchanged")
                        : result.error.status === 409
                          ? t("history.conflict")
                          : result.error.status === 0 ||
                              result.error.status >= 500
                            ? t("history.uncertain")
                            : result.error.message}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p>{t("history.bulkHelp")}</p>
            <label className="field">
              {t("history.newStatus")}
              <select
                disabled={mutation.isPending}
                value={status}
                onChange={(event) => {
                  const next = decodeUnknownOption(
                    bulkStatusSchema,
                    event.target.value,
                  );
                  if (next) setStatus(next);
                }}
              >
                {bulkStatusSchema.literals.map((value) => (
                  <option key={value} value={value}>
                    {orderStatus(value)}
                  </option>
                ))}
              </select>
            </label>
            <p className="muted">{t("history.noBulkUndo")}</p>
            <ul className="bulk-results">
              {orders.map((order) => (
                <li key={order.id}>
                  <strong>{order.label}</strong>
                  <span>
                    {orderStatus(order.state)} → {orderStatus(status)}
                  </span>
                </li>
              ))}
            </ul>
            {mutation.error && (
              <p role="alert" className="alert">
                {mutation.error.message}
              </p>
            )}
          </>
        )}
      </div>
      <footer className="dialog-footer">
        <button
          className="secondary"
          disabled={mutation.isPending}
          onClick={close}
        >
          {t(results ? "common.done" : "common.back")}
        </button>
        {!results && (
          <button
            className={`primary ${status === "order_canceled" ? "danger" : ""}`}
            disabled={mutation.isPending || started.current}
            onClick={() => {
              if (started.current) return;
              started.current = true;
              mutation.mutate();
            }}
          >
            {t(mutation.isPending ? "history.updating" : "history.confirm", {
              count: orders.length,
            })}
          </button>
        )}
      </footer>
    </StaffDialog>
  );
}
